<template>
    <div class="overflow-visible">

        <div    v-if="!currentNotificationSettings.loading">
            <!-- Notifications Enabled -->
            <banner-error
                v-if="!currentNotificationSettings.enabled"
                class="mb-2"
            >
                <template #title>Notifications Disabled</template>
                <template #content>
                    This user will <strong>NOT</strong> receive alarm notifications.
                </template>
            </banner-error>
            <banner-error
                v-if="!hasAnyMethodsEnabled && currentNotificationSettings.enabled"
                class="mb-2"
            >
                <template #title>No Notification Methods Enabled</template>
                <template #content>
                    You'll need to add at least one notification method for notifications to be enabled.
                </template>
            </banner-error>
            <h5 class="text-left text-sm font-semibold text-gray-800 mt-4 mb-1">Notifications</h5>
            <div class="mt-1 sm:mt-0 sm:col-span-2">
                <span class="relative z-0 flex shadow-sm rounded-md w-full lg:w-auto">
                    <button     type="button"
                                @click="toggleNotificationsEnabled"
                                :class="currentNotificationSettings.enabled ? 'bg-blue-200' : 'bg-white'"
                                class="relative flex-1 inline-flex justify-center items-center px-4 py-2 rounded-l-md border border-gray-300 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                        Enabled
                    </button>
                    <button     type="button"
                                @click="toggleNotificationsEnabled"
                                :class="!currentNotificationSettings.enabled ? 'bg-blue-200' : 'bg-white'"
                                class="-ml-px relative flex-1 inline-flex justify-center items-center px-4 py-2 rounded-r-md border border-gray-300 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                        Disabled
                    </button>
                </span>
            </div>



            <div    v-if="currentNotificationSettings.enabled">
                <!-- Notification Methods -->
                <div class="mt-6">
                    <div class="border-t border-gray-200"></div>
                    <h5 class="text-left text-sm font-semibold text-gray-800 mt-4 mb-4">Methods to Send Notifications</h5>

                    <!-- Email -->
                    <div v-if="hasEmailFeature" class="mb-6">
                        <span class="flex items-center gap-2 font-medium text-sm text-gray-700 mb-2">
                            <span :class="iconEmail" style="font-size: 1.1rem"></span> Email
                        </span>
                        <div v-for="(notification, index) in currentNotificationSettings.methods.email"
                             :key="`email_${index}`"
                             class="flex items-center">
                            <floating-input v-model:value="currentNotificationSettings.methods['email'][index]"
                                            label="Email Address"
                                            class="w-full"
                                            :id="`email_input_${index}`"
                            ></floating-input>
                            <span @click="deleteNotification('email', index)"
                                  class="cursor-pointer ml-2 pb-2 lnr icon-trash text-gray-400 hover:text-red-500"></span>
                        </div>
                        <button-blue-secondary @click="createNotification('email')" class="mt-2 w-full">
                            + Add Email Address
                        </button-blue-secondary>
                    </div>

                    <!-- SMS -->
                    <div v-if="hasSmsFeature" class="mb-6 pt-4 border-t border-gray-100">
                        <span class="flex items-center gap-2 font-medium text-sm text-gray-700 mb-2">
                            <span :class="iconSMS" style="font-size: 1.1rem"></span> Text Message
                        </span>
                        <div v-for="(notification, index) in currentNotificationSettings.methods.text"
                             :key="`text_${index}`"
                             class="flex items-center">
                            <floating-input v-model:value="currentNotificationSettings.methods.text[index]"
                                            label="Phone Number (XXXXXXXXXX)"
                                            class="w-full"
                                            :id="`text_input_${index}`"
                            ></floating-input>
                            <span @click="deleteNotification('text', index)"
                                  class="cursor-pointer ml-2 pb-2 lnr icon-trash text-gray-400 hover:text-red-500"></span>
                        </div>
                        <button-blue-secondary @click="createNotification('text')" class="mt-2 w-full">
                            + Add Phone Number
                        </button-blue-secondary>
                    </div>

                    <!-- Phone Call -->
                    <div v-if="hasVoiceFeature" class="mb-6 pt-4 border-t border-gray-100">
                        <div class="mb-3">
                            <span class="flex items-center gap-2 font-medium text-sm text-gray-700">
                                <span :class="iconVoiceMail" style="font-size: 1.1rem"></span> Phone Call
                            </span>
                            <!-- Phone calls still alert the user when comments are required; they just
                                 can't resolve or snooze from the call, so point them to web/mobile. -->
                            <p v-if="requireAlarmComment" class="m-0 mt-1 text-xs leading-relaxed text-gray-500">
                                Phone calls will still alert you. Resolving or snoozing requires the web or
                                mobile app while "Require Comment on Alarm Actions" is enabled, because a
                                phone caller cannot enter a required comment.
                            </p>
                        </div>
                        <div v-for="(notification, index) in currentNotificationSettings.methods.voice"
                             :key="`voice_${index}`"
                             class="flex items-center gap-3 mb-3 last:mb-0">
                            <floating-input v-model:value="currentNotificationSettings.methods.voice[index]"
                                            label="Phone Number (XXXXXXXXXX)"
                                            class="w-full"
                                            :id="`voice_input_${index}`"
                            ></floating-input>
                            <span @click="deleteNotification('voice', index)"
                                  class="shrink-0 cursor-pointer lnr icon-trash text-gray-400 hover:text-red-500"></span>
                        </div>
                        <button-blue-secondary @click="createNotification('voice')" class="mt-3 w-full">
                            + Add Phone Number
                        </button-blue-secondary>
                    </div>

                    <!-- Push Notifications -->
                    <div class="mb-2 pt-4 border-t border-gray-100">
                        <span class="flex items-center gap-2 font-medium text-sm text-gray-700 mb-2">
                            <span :class="iconPushNotification" style="font-size: 1.1rem"></span> Push Notifications
                        </span>
                        <div class="mt-1">
                            <loading v-if="loadingPushNotificationModels" width="w-full"></loading>
                            <select2 v-else
                                :multiple="true"
                                :options="pushNotificationArray"
                                :value="currentNotificationSettings.methods['push']"
                                name="pushMethodSelect"
                                @input="this.updatePushNotifications"
                            ></select2>
                        </div>
                    </div>
                </div>

                <div class="mt-8">
                    <div class="border-t border-gray-200"></div>
                    <user-notification-times-edit   v-if="currentNotificationSettings.timesLoaded"
                                                v-model:times="currentNotificationSettings.times"
                                                v-model:server-times="currentNotificationSettings.serverTimes"
                                                v-model:time-choice="currentNotificationSettings.timeChoice"
                ></user-notification-times-edit>
                </div>

                <div class="mt-6">
                    <div class="border-t border-gray-200"></div>
                    <h5 class="text-left text-sm font-semibold text-gray-800 mt-4 mb-2">Alarm Escalation and Repeating</h5>
                    <p class="text-sm leading-5 text-gray-600">
                        Alarm Notification Delay is the time between when an alarm is generated and when the first notification is sent.
                    </p>
                    <p class="text-sm leading-5 text-gray-600">
                        Re-notification Interval is the time between notifications when an alarm is active (not snoozed or resolved).
                    </p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-2">
                        <floating-input     v-model:value="currentNotificationSettings.delayTime"
                                            id="delay_time"
                                            label="Alarm Notification Delay (Minutes)"
                                            :readonly="! canEditAlarmReminder"
                        ></floating-input>
                        <floating-input     v-model:value="currentNotificationSettings.renotificationTime"
                                            id="renotification_time"
                                            label="Re-notification Interval (Minutes)"
                                            :readonly="! canEditAlarmReminder"
                        ></floating-input>
                    </div>
                </div>

                <div v-if="unSnoozeNotificationEnabled" class="mt-6">
                    <div class="border-t border-gray-200"></div>
                    <h5 class="text-left text-sm font-semibold text-gray-800 mt-4 mb-2">Un-snooze Notifications</h5>
                    <p class="text-sm leading-5 text-gray-600">
                        These Notifications will be sent when an alarm you are setup to receive notifications for is unsnoozed.
                    </p>
                    <unsnooze-notification-radio
                        v-model:unsnooze-notification-setting="currentNotificationSettings.unsnoozeNotificationType"
                    ></unsnooze-notification-radio>
                </div>

                <!-- Notification on Resolve -->
                <div v-if="resolvedAlarmNotificationsEnabled || mandatoryResolvedAlarmNotifications">
                <h5 class="border-t border-gray-200 text-left text-sm font-semibold text-gray-800 mt-6 pt-2 mb-2">Resolved Alarm Notifications</h5>
                <p class="text-sm leading-5 text-gray-600">
                    Receive an email to your account's primary email address when an alarm is resolved.
                </p>
                <div v-if="mandatoryResolvedAlarmNotifications"
                     class="mt-2 rounded-md bg-blue-50 p-3 text-sm text-blue-800">
                    Resolved alarm notifications are required on this site. This user will receive them automatically.
                </div>
                <div    v-else>
                <span class="mt-2 relative z-0 inline-flex shadow-sm rounded-md">
                        <button :class="currentNotificationSettings.notifyOnResolved ? 'bg-blue-200' : 'bg-white'"
                                class="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700"
                                type="button"
                                @click="currentNotificationSettings.notifyOnResolved = true">
                            Enabled
                        </button>
                        <button :class="!currentNotificationSettings.notifyOnResolved ? 'bg-blue-200' : 'bg-white'"
                                class="-ml-px relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700"
                                type="button"
                                @click="currentNotificationSettings.notifyOnResolved = false">
                            Disabled
                        </button>
                    </span>
                </div>
                </div>
            </div>

        </div>
        <div    v-else>
            Loading Notifications for User ... Please wait.
        </div>
    </div>
</template>

<script>

import {iconNamesMixin} from '@/mixins/IconNamesMixin';

import Toggle from "@/components/elements/toggle";
import FloatingInput from "@/components/elements/FloatingInput";
import UserNotificationTimesEdit from "@/components/Users/UserNotificationTimesEdit";
import axios from "axios";
import Select2 from "@/components/elements/select2";
import Loading from "@/components/elements/loading";
import {Features} from "@/mixins/AuthorizationHelpers/Features";
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import BannerError from "../Banners/BannerError.vue";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary";
import UnsnoozeNotificationRadio from "./UnsnoozeNotificationRadio.vue";

export default {
    name: "UserSetupNotifications",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {
        UnsnoozeNotificationRadio,
        BannerError, ButtonBlueSecondary, Loading, Select2, UserNotificationTimesEdit, FloatingInput, Toggle},
    emits: ['update:currentNotificationSettings'],
    compatConfig: { COMPONENT_V_MODEL: false },
    props: {
        userId: Number,
        currentNotificationSettings: Object,
        companyId: String,
        unSnoozeNotificationEnabled: Boolean,
        resolvedAlarmNotificationsEnabled: {
            type: Boolean,
            default: false,
        },
        mandatoryResolvedAlarmNotifications: {
            type: Boolean,
            default: false,
        },
        requireAlarmComment: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            pushNotifications: {},
            loadingPushNotificationModels: true,
        }
    },
    created() {
        this.getMobilePushInfo();
    },
    mounted() {
    },
    methods: {
        createNotification(type) {
            let notifs = this.currentNotificationSettings
            notifs.methods[type].push('')
            this.updateNotifications(notifs)
        },
        deleteNotification(type, index) {
            let notifs = this.currentNotificationSettings
            notifs.methods[type].splice(index, 1)
            this.updateNotifications(notifs)
        },
        updateNotifications(notifs) {
            this.$emit('update:currentNotificationSettings', notifs)
            this.$forceUpdate()
        },
        toggleNotificationsEnabled() {
            let notifs = this.currentNotificationSettings
            notifs.enabled = !notifs.enabled
            this.updateNotifications(notifs)
        },
        getMobilePushInfo() {
            axios.get(this.company.buildCompanyApiInternalRoute(`users/${this.userId}/mobile-push-info`)).then(response => {
                this.pushNotifications = response.data.data;
                this.loadingPushNotificationModels = false;
            }).catch(error => {

            }).then(() => {

            })
        },
        updatePushNotifications() {
            let newPushMethods = [];
            let elementName = "pushMethodSelect"
            let val = $('[name=' + elementName + ']').val()
            val.forEach((item, key) => {
                newPushMethods.push(parseInt(item))
            })
            this.currentNotificationSettings.methods['push'] = newPushMethods;
            this.updateNotifications(this.currentNotificationSettings);
        },

    },
    computed: {
        pushNotificationArray(){
            let result = {};
            _.map(this.pushNotifications, (val, key, coll) => {
                result[val['id']] = val['device_name_pretty'];
            })
            return result;
        },
        hasEmailFeature() {
            return this.featureLevel.hasFeature(Features.NOTIFICATION_METHODS_EMAIL, this.companyId)
        },
        hasSmsFeature() {
            return this.featureLevel.hasFeature(Features.NOTIFICATION_METHODS_SMS, this.companyId)
        },
        hasVoiceFeature() {
            return this.featureLevel.hasFeature(Features.NOTIFICATION_METHODS_VOICE, this.companyId)
        },
        hasPushFeature() {
            return this.featureLevel.hasFeature(Features.NOTIFICATION_METHODS_PUSH, this.companyId)
        },
        hasAnyMethodsEnabled(){
          // Check to see if any this.currentNotificationSettings[key][] has length > 0 and items with length > 1
          let methods = this.currentNotificationSettings.methods
          let hasAny = false
          for (let key in methods) {
            if (methods[key].length > 0) {
              for (let i = 0; i < methods[key].length; i++) {
                if (methods[key][i].length > 0) {
                  hasAny = true
                  break
                }
              }
            }
          }
          return hasAny
        },
        canEditAlarmReminder() {
            return this.permissions.can('edit_alarm_reminder')
                || this.permissions.can('edit_alarm_reminder_via_user_edit_only')
                || this.permissions.can('edit_alarm_reminder_self');
        }
    },
    watch: {
        notificationsEnabled() {
            let notifs = this.currentNotificationSettings
            notifs['enabled'] = this.notificationsEnabled
            this.updateNotifications(notifs)
        },
    },
}
</script>

<style scoped>

</style>